import React, { useEffect, useRef, useMemo, Fragment } from 'react';
import { Helmet } from 'react-helmet';
import { MonitorConfiguration, Room, Patient } from '@/components/pages/kiosk/monitor/monitor.types';
import CacheManager from '@/graph/cacheManager';
import { useApolloClient, NormalizedCacheObject, ApolloClient } from '@apollo/client';
import { useAlerts } from './hooks/useAlerts';
import MonitorContent from './MonitorContent';
import ClientUpdater from '../../../ClientUpdater';
import { useScreenConfiguration } from '@/contexts/ScreenConfigurationContext';

export interface MonitorProps {
  operationRooms?: Room[];
  waitingRoom?: Room;
  preOp?: Room;
  pacu?: Room;
  postOps?: Room[];
  configuration?: MonitorConfiguration;
  scope?: {
    hospital?: {
      id?: string;
      groupId?: string;
      modules?: {
        preOpPriority?: { hasPreOpPriority?: boolean };
        noteTablet?: boolean;
        hasPostop?: boolean;
        showStaffTracking?: boolean;
      };
    };
  };
  session?: {
    account?: {
      metadata?: {
        staffCostAccess?: boolean;
      };
    };
  };
  isSuperAdmin?: boolean;
  isGroupAdmin?: boolean;
  isLiaison?: boolean;
  isKiosk?: boolean;
  staffMode?: boolean;
  loading?: boolean;
  patientNameFormat?: string;
}

export const MonitorNew: React.FC<MonitorProps> = props => {
  const { scope, operationRooms = [], waitingRoom, preOp, pacu, postOps = [], staffMode, loading } = props;

  const cacheManagerRef = useRef<CacheManager | null>(null);
  const client = useApolloClient();

  // Initialize CacheManager
  useEffect(() => {
    if (!cacheManagerRef.current && client) {
      console.log('🔧 Initializing CacheManager');
      // ApolloClient is configured in JS and has no typing
      cacheManagerRef.current = new CacheManager(client as ApolloClient<NormalizedCacheObject>);
    }
  }, [client]);

  // Handle cache management
  useEffect(() => {
    if (cacheManagerRef.current && !loading) {
      const roomData = {
        waitingRoom,
        preOp,
        pacu,
        postOps,
        operationRooms,
      };
      cacheManagerRef.current.monitorAndPurge(roomData);
    } else if (!cacheManagerRef.current) {
      console.warn('❌ CacheManager not initialized - no Apollo client available');
    }
  }, [loading, waitingRoom, preOp, pacu, postOps, operationRooms]);

  useAlerts(props);

  const {
    showPatientTypes: showPatientTypesRaw,
    showStaffIndicators,
    showPostOp,
    showLatePreOpIndicator,
  } = useScreenConfiguration();

  const showPatientTypes = useMemo(() => showPatientTypesRaw || [], [showPatientTypesRaw]);

  // Memoized computed values for rendering
  const hasPreOpPriorityModule = scope?.hospital?.modules?.preOpPriority?.hasPreOpPriority || false;
  const hasNoteModule = scope?.hospital?.modules?.noteTablet || false;

  const filteredWaitingRoom = useMemo(
    () => filterPatients(waitingRoom, showPatientTypes),
    [showPatientTypes, waitingRoom]
  );

  const filteredPreOp = useMemo(() => filterPatients(preOp, showPatientTypes), [preOp, showPatientTypes]);

  const filteredPacu = useMemo(() => filterPatients(pacu, showPatientTypes), [pacu, showPatientTypes]);

  const filteredPostOps = useMemo(
    () => postOps.filter(room => filterPatients(room, showPatientTypes)).filter(isDefined),
    [postOps, showPatientTypes]
  );

  const orderedRooms = useMemo(() => {
    const filteredOperationRooms = operationRooms
      .map(room => {
        if (!showPatientTypes || !showPatientTypes.length) {
          return room;
        }
        return {
          ...room,
          patient: room.patient && showPatientTypes.includes(room.patient.type || '') ? room.patient : null,
          patients: room.patients ? room.patients.filter(p => showPatientTypes.includes(p.type || '')) : [],
        };
      })
      .filter(isDefined);

    const roomsWithOrder = filteredOperationRooms
      .filter(room => room?.order !== null)
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    const roomsWithoutOrder = filteredOperationRooms.filter(room => room?.order === null);

    return [
      ...roomsWithOrder,
      ...roomsWithoutOrder.filter(room => room?.name?.toUpperCase()?.startsWith('OR')),
      ...roomsWithoutOrder.filter(room => !room?.name?.toUpperCase()?.startsWith('OR')),
    ];
  }, [operationRooms, showPatientTypes]);

  const shouldShowClientUpdater = useMemo(
    () =>
      [...orderedRooms, filteredWaitingRoom, filteredPreOp, filteredPacu, ...filteredPostOps]
        .filter(isRoomWithPatients)
        .every(room => room.patients.length === 0),
    [orderedRooms, filteredWaitingRoom, filteredPreOp, filteredPacu, filteredPostOps]
  );

  const staffCostAccess = props.session?.account?.metadata?.staffCostAccess;
  const showStaffTracking = useMemo(
    () =>
      scope?.hospital?.modules?.showStaffTracking &&
      (props.isSuperAdmin || staffCostAccess || (showStaffIndicators && props.isKiosk)),
    [
      scope?.hospital?.modules?.showStaffTracking,
      props.isSuperAdmin,
      staffCostAccess,
      showStaffIndicators,
      props.isKiosk,
    ]
  );

  return (
    <Fragment>
      <Helmet>
        <meta name="viewport" content="width=1200, initial-scale=0" />
      </Helmet>
      <MonitorContent
        operationRooms={orderedRooms}
        waitingRoom={filteredWaitingRoom}
        preOp={filteredPreOp}
        pacu={filteredPacu}
        postOps={filteredPostOps}
        hasPostop={scope?.hospital?.modules?.hasPostop && showPostOp !== false}
        hasNoteModule={hasNoteModule}
        hasPreOpPriorityModule={hasPreOpPriorityModule}
        isMini={false}
        isSuperAdmin={props.isSuperAdmin}
        isLiaison={props.isLiaison}
        scope={scope}
        staffMode={staffMode}
        showStaffTracking={!!showStaffTracking}
        showLatePreOpIndicator={!!showLatePreOpIndicator || !props.isKiosk}
      />
      {shouldShowClientUpdater && <ClientUpdater />}
    </Fragment>
  );
};

const isDefined = <T = any,>(value: T): value is T => value != null;

const isRoomWithPatients = (room: any): room is Room & { patients: Patient[] } =>
  room && typeof room === 'object' && Array.isArray(room.patients);

const filterPatients = (room?: Room, showPatientTypes?: string[]) => {
  if (!room || !room.patients) return room;
  if (!showPatientTypes || !showPatientTypes.length) {
    return room;
  }
  return {
    ...room,
    patients: room.patients.filter(p => showPatientTypes.includes(p.type || '')),
  };
};
